/** @file Utility function for working with strings. */
import { AssertionError } from './assert';
/**
 * Concat two strings together.
 *
 * @param a The first string.
 * @param b The second string.
 * @returns It simply returns `a + b`, but the type is better.
 */
export function concatString(a, b) {
    return `${a}${b}`;
}
/**
 * Capitalizes the first letter of a given string.
 *
 * This function:
 * 1. Converts the first character of the string to uppercase.
 * 2. Appends the rest of the string unchanged.
 *
 * @param str The string to capitalize.
 * @returns The input string with the first letter capitalized.
 *
 * @example
 * capitalize('hello'); // Returns 'Hello'
 * capitalize('world'); // Returns 'World'
 * capitalize(''); // Returns ''
 */
export function capitalize(str) {
    if (str.length === 0)
        return str;
    return concatString(str[0].toUpperCase(), str.substring(1));
}
/**
 * Split a string into lines, taking possible carriage returns into account.
 *
 * @param source The source string to split.
 * @returns The lines of the source string.
 */
export function splitLines(source) {
    return source.split(/\r?\n/);
}
/**
 * Splits a string into an array of substrings using a specified delimiter, while supporting escape
 * sequences.
 *
 * The function:
 * 1. Splits the input string by the given `splitToken`.
 * 2. Handles escaped split tokens (e.g., `\\` or `\splitToken`), ensuring they are not treated as
 *    delimiters.
 * 3. Returns an array of substrings, including empty strings for consecutive split tokens.
 *
 * @param str The input string to split.
 * @param splitToken A single character used as the delimiter.
 * @returns An array of substrings split by the `splitToken`.
 *
 * @example
 * splitEscapableString('foo#bar#baz', '#'); // Returns ['foo', 'bar', 'baz']
 * splitEscapableString('foo\\#bar#baz', '#'); // Returns ['foo#bar', 'baz']
 * splitEscapableString('foo###baz', '#'); // Returns ['foo', '', '', 'baz']
 */
export function splitEscapableString(str, splitToken) {
    AssertionError.assert(splitToken.length === 1);
    const parts = [];
    const curPart = [];
    let subSeqStart = 0;
    /**
     * Push the current sequence.
     *
     * @param i The end of the current sequence, from the start.
     */
    const pushCurPart = (i) => {
        curPart.push(str.substring(subSeqStart, i));
        subSeqStart = i + 1;
    };
    for (let i = 0; i < str.length; i++) {
        const cur = str[i];
        if (cur === '\\') {
            pushCurPart(i);
            // Skip next char
            i += 1;
        }
        else if (cur === '#') {
            pushCurPart(i);
            parts.push(curPart.join(''));
            curPart.length = 0;
        }
    }
    pushCurPart(str.length);
    parts.push(curPart.join(''));
    return parts;
}
/**
 * Converts a number to its ordinal string representation.
 *
 * The function returns the number followed by its ordinal suffix (e.g., 'st', 'nd', 'rd', 'th').
 *
 * @param n The number to convert to an ordinal string.
 * @returns The ordinal string representation of the number.
 *
 * @example
 * toOrdinal(1); // Returns 'st'
 * toOrdinal(2); // Returns 'nd'
 * toOrdinal(3); // Returns 'rd'
 * toOrdinal(4); // Returns 'th'
 */
export function toOrdinal(n) {
    return ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th';
}
/**
 * Check if a string contains only whitespace characters.
 *
 * We use the regular expression `/^\s*$/` to match strings.
 *
 * @param str The string to check if it's only whitespace.
 * @returns A boolean indicating whether the string is only whitespace.
 */
export function isWhiteSpaceOnly(str) {
    return /^\s*$/.test(str);
}
